import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { TipoLogisticaEnum } from '../../../../shared/enums/vendas/tipo-logistica.enum';
import { TipoContatoEnum } from '../../../../shared/enums/pessoa/tipo-contato.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { UfOption, UnidadeFederativaService } from '../../../../shared/services/apis/usuario/unidade-federativa.service';
import { FormaEnvioService } from '../../../../shared/services/apis/vendas/forma-envio.service';
import { FormaFreteItem, ServicoLogisticaReversaItem } from '../../../../shared/services/models/vendas/forma-envio-detalhe.model';
import { TipoDocumentoImpressao, TipoEntrega, TipoLogistica } from '../../../../shared/services/responses/vendas/forma-envio-lookups.response';
import { FormaEnvioCorreiosRequest, FormaEnvioSalvarRequest } from '../../../../shared/services/requests/vendas/forma-envio.request';
import { BuscarServicosLogisticaRequest } from '../../../../shared/services/requests/vendas/buscar-servicos-logistica.request';
import { environment } from '../../../../../environments/environment';
import { FormaFreteDialogComponent, FormaFreteDialogData } from './dialogs/forma-frete-dialog/forma-frete-dialog.component';
import { BuscarFretesDialogComponent, BuscarFretesDialogData } from './dialogs/buscar-fretes-dialog/buscar-fretes-dialog.component';
import { ServicoReversaDialogComponent, ServicoReversaDialogData } from './dialogs/servico-reversa-dialog/servico-reversa-dialog.component';
import { ServicoLogisticaItem } from '../../../../shared/services/responses/vendas/buscar-servicos-logistica.response';
import { MaskService } from '../../../../shared/services/mask.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { cnpjValidator } from '../../../../shared/validators/cnpj.validator';
import { EtiquetaLookupService } from '../../../../shared/services/apis/etiqueta/etiqueta-lookup.service';
import { EtiquetaLookupItemStruct } from '../../../../shared/services/structs/etiqueta/etiqueta-lookup-item.struct';
import { TipoEtiquetaEnum } from '../../../../shared/enums/etiqueta/tipo-etiqueta.enum';

@Component({
  selector: 'app-forma-envio-cadastro',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTableModule,
    FooterActionComponent,
    HeaderComponent,
    LoadingBoxComponent,
    SelectPesquisavelComponent,
    AlertCardComponent,
    NgxMaskDirective,
  ],
  templateUrl: './forma-envio-cadastro.component.html',
  styleUrl: './forma-envio-cadastro.component.css',
  providers: [provideNgxMask()],
})
export class FormaEnvioCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public form: FormGroup;
  public transportadorCorreiosControl = new FormControl<number | null>(null);
  public isLoading = false;
  public isSaving = false;
  public isEdicao = false;
  public isBuscarFretesLoading = false;
  public idFormaEnvio = 0;
  public tiposLogistica: TipoLogistica[] = [];
  public tiposEntrega: TipoEntrega[] = [];
  public tiposDocumentoImpressao: TipoDocumentoImpressao[] = [];
  public listaUf: UfOption[] = [];
  public formasFrete: FormaFreteItem[] = [];
  public servicosReversa: ServicoLogisticaReversaItem[] = [];
  public etiquetasTransportadora: EtiquetaLookupItemStruct[] = [];
  public masks = MaskService.getMasks();

  public readonly urlTransportador = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Transportadora}`;
  public readonly tipoLogisticaEnum = TipoLogisticaEnum;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private formaEnvioService: FormaEnvioService,
    private unidadeFederativaService: UnidadeFederativaService,
    private alertService: AlertService,
    private etiquetaLookupService: EtiquetaLookupService
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.setupFieldReactions();
    this.carregarLookups();
    this.route.params.subscribe(params => {
      if (params['idFormaEnvio']) {
        this.idFormaEnvio = +params['idFormaEnvio'];
        this.isEdicao = true;
        this.carregar();
      }
    });
  }

  /** Reações reativas: CNPJ obrigatório quando "utilizar outro CNPJ" e limpeza ao trocar de tipo logística. */
  private setupFieldReactions(): void {
    this.form.get('utilizarOutroCnpj')?.valueChanges.subscribe((utiliza: boolean) => {
      if (!utiliza) this.form.get('cnpjCorreios')?.reset(null, { emitEvent: false });
      this.applyCnpjValidators(utiliza);
    });

    this.form.get('idTipoLogistica')?.valueChanges.subscribe(() => this.resetCamposEspecificos());
  }

  /** CNPJ sempre validado pelo dígito verificador; obrigatório apenas quando utiliza outro CNPJ. */
  private applyCnpjValidators(utilizarOutroCnpj: boolean): void {
    const cnpj = this.form.get('cnpjCorreios');
    cnpj?.setValidators(utilizarOutroCnpj ? [Validators.required, cnpjValidator()] : [cnpjValidator()]);
    cnpj?.updateValueAndValidity({ emitEvent: false });
  }

  /** Limpa todos os campos específicos de tipo (Correios + fretes/reversa) ao trocar de tipo logística. */
  private resetCamposEspecificos(): void {
    this.form.patchValue({
      possuiContrato: false,
      utilizarOutroCnpj: false,
      usuarioCorreios: null,
      senhaCorreios: null,
      cnpjCorreios: null,
      habilitarAtualizacaoAutomaticaRastreios: false,
      utilizaDCe: false,
      numeroCartaoPostagem: null,
      unidadePostagem: null,
      diretoriaRegional: null,
      utilizarDataExpedicaoPostagem: false,
      codigoAdministrativo: null,
      numeroContrato: null,
      anoContrato: null,
      nomeCliente: null,
      utilizarDimensoesMinimas: false,
      agruparProdutosEmbalagem: false,
      isMarcarMinimoValorDeclarado: false,
      isMarcarMinimoValorDeclaradoMini: false,
      prazoPostagemConvencional: null,
      usuarioLogisticaReversa: null,
      senhaLogisticaReversa: null,
      prazoPostagemReversa: null,
    }, { emitEvent: false });
    this.transportadorCorreiosControl.reset(null, { emitEvent: false });
    this.applyCnpjValidators(false);
    this.formasFrete = [];
    this.servicosReversa = [];
  }

  private createForm(): void {
    this.form = this.fb.group({
      idTipoLogistica: [TipoLogisticaEnum.Customizado, Validators.required],
      descricao: ['', Validators.required],
      isAtivo: [true],
      urlRastreamento: [null],
      impressaoEmbalagem: [[]],
      impressaoExpedicao: [[]],
      idModeloEtiqueta: [null],
      // Correios
      possuiContrato: [false],
      utilizarOutroCnpj: [false],
      usuarioCorreios: [null],
      senhaCorreios: [null],
      cnpjCorreios: [null, cnpjValidator()],
      habilitarAtualizacaoAutomaticaRastreios: [false],
      utilizaDCe: [false],
      numeroCartaoPostagem: [null],
      unidadePostagem: [null],
      diretoriaRegional: [null],
      utilizarDataExpedicaoPostagem: [false],
      codigoAdministrativo: [null],
      numeroContrato: [null],
      anoContrato: [null],
      nomeCliente: [null],
      utilizarDimensoesMinimas: [false],
      agruparProdutosEmbalagem: [false],
      isMarcarMinimoValorDeclarado: [false],
      isMarcarMinimoValorDeclaradoMini: [false],
      prazoPostagemConvencional: [null],
      usuarioLogisticaReversa: [null],
      senhaLogisticaReversa: [null],
      prazoPostagemReversa: [null],
    });
  }

  private carregarLookups(): void {
    this.formaEnvioService.getLookups().subscribe({
      next: res => {
        if (!res.isError) {
          this.tiposLogistica = res.tiposLogistica;
          this.tiposEntrega = res.tiposEntrega;
          this.tiposDocumentoImpressao = res.tiposDocumentoImpressao;
        }
      },
    });
    this.unidadeFederativaService.getUnidadesFederativas().subscribe(unidadesFederativas => {
      this.listaUf = unidadesFederativas;
    });
    this.etiquetaLookupService
      .listEtiquetaSelect(undefined, TipoEtiquetaEnum.Transportadora)
      .subscribe({
        next: res => {
          if (!res.isError) this.etiquetasTransportadora = res.list;
        },
      });
  }

  private carregar(): void {
    this.isLoading = true;
    this.formaEnvioService.getById(this.idFormaEnvio).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError || !res.formaEnvio) {
          this.alertService.show('Erro', res.errorDescription || 'Forma de envio não encontrada.', AlertType.error);
          this.voltar();
          return;
        }
        const fe = res.formaEnvio;
        this.form.patchValue({
          idTipoLogistica: fe.idTipoLogistica,
          descricao: fe.descricao,
          isAtivo: fe.isAtivo,
          urlRastreamento: fe.urlRastreamento,
          impressaoEmbalagem: fe.idsDocumentosImpressaoEmbalagem ?? [],
          impressaoExpedicao: fe.idsDocumentosImpressaoExpedicao ?? [],
          idModeloEtiqueta: fe.idModeloEtiqueta ?? null,
        }, { emitEvent: false });
        this.form.get('idTipoLogistica')?.disable({ emitEvent: false });

        if (fe.correios) {
          const c = fe.correios;
          this.form.patchValue({
            possuiContrato: c.possuiContrato,
            utilizarOutroCnpj: c.utilizarOutroCnpj,
            usuarioCorreios: c.usuarioCorreios,
            senhaCorreios: c.senhaCorreios,
            cnpjCorreios: c.cnpjCorreios,
            habilitarAtualizacaoAutomaticaRastreios: c.habilitarAtualizacaoAutomaticaRastreios,
            utilizaDCe: c.utilizaDCe,
            numeroCartaoPostagem: c.numeroCartaoPostagem,
            unidadePostagem: c.unidadePostagem,
            diretoriaRegional: c.diretoriaRegional,
            utilizarDataExpedicaoPostagem: c.utilizarDataExpedicaoPostagem,
            codigoAdministrativo: c.codigoAdministrativo,
            numeroContrato: c.numeroContrato,
            anoContrato: c.anoContrato,
            nomeCliente: c.nomeCliente,
            utilizarDimensoesMinimas: c.utilizarDimensoesMinimas,
            agruparProdutosEmbalagem: c.agruparProdutosEmbalagem,
            isMarcarMinimoValorDeclarado: c.isMarcarMinimoValorDeclarado,
            isMarcarMinimoValorDeclaradoMini: c.isMarcarMinimoValorDeclaradoMini,
            prazoPostagemConvencional: c.prazoPostagemConvencional,
            usuarioLogisticaReversa: c.usuarioLogisticaReversa,
            senhaLogisticaReversa: c.senhaLogisticaReversa,
            prazoPostagemReversa: c.prazoPostagemReversa,
          }, { emitEvent: false });
          this.applyCnpjValidators(c.utilizarOutroCnpj);
          if (c.idPessoaTransportador) {
            this.transportadorCorreiosControl.setValue(c.idPessoaTransportador);
          }
        }
        this.formasFrete = [...(fe.formasFrete ?? [])];
        this.servicosReversa = [...(fe.servicosLogisticaReversa ?? [])];
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao carregar forma de envio.', AlertType.error);
        this.voltar();
      },
    });
  }

  public get isCorreios(): boolean {
    return (this.form.getRawValue().idTipoLogistica) === TipoLogisticaEnum.Correios;
  }

  public get isCustomizado(): boolean {
    return (this.form.getRawValue().idTipoLogistica) === TipoLogisticaEnum.Customizado;
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }
    const raw = this.form.getRawValue();
    const request = new FormaEnvioSalvarRequest();
    request.idTipoLogistica = raw.idTipoLogistica;
    request.descricao = raw.descricao;
    request.isAtivo = raw.isAtivo;
    request.urlRastreamento = raw.urlRastreamento || null;

    request.idsDocumentosImpressaoEmbalagem = raw.impressaoEmbalagem ?? [];
    request.idsDocumentosImpressaoExpedicao = raw.impressaoExpedicao ?? [];
    request.idModeloEtiqueta = raw.idModeloEtiqueta ?? null;

    request.formasFrete = this.formasFrete;
    request.servicosLogisticaReversa = this.servicosReversa;

    if (this.isCorreios) {
      const correios = new FormaEnvioCorreiosRequest();
      correios.possuiContrato = raw.possuiContrato;
      correios.idPessoaTransportador = this.transportadorCorreiosControl.value ?? null;
      correios.utilizarOutroCnpj = raw.utilizarOutroCnpj;
      correios.usuarioCorreios = raw.usuarioCorreios || null;
      correios.senhaCorreios = raw.senhaCorreios || null;
      correios.cnpjCorreios = raw.cnpjCorreios || null;
      correios.habilitarAtualizacaoAutomaticaRastreios = raw.habilitarAtualizacaoAutomaticaRastreios;
      correios.utilizaDCe = raw.utilizaDCe;
      correios.numeroCartaoPostagem = raw.numeroCartaoPostagem || null;
      correios.unidadePostagem = raw.unidadePostagem || null;
      correios.diretoriaRegional = raw.diretoriaRegional || null;
      correios.utilizarDataExpedicaoPostagem = raw.utilizarDataExpedicaoPostagem;
      correios.codigoAdministrativo = raw.codigoAdministrativo || null;
      correios.numeroContrato = raw.numeroContrato || null;
      correios.anoContrato = raw.anoContrato || null;
      correios.nomeCliente = raw.nomeCliente || null;
      correios.utilizarDimensoesMinimas = raw.utilizarDimensoesMinimas;
      correios.agruparProdutosEmbalagem = raw.agruparProdutosEmbalagem;
      correios.isMarcarMinimoValorDeclarado = raw.isMarcarMinimoValorDeclarado;
      correios.isMarcarMinimoValorDeclaradoMini = raw.isMarcarMinimoValorDeclaradoMini;
      correios.prazoPostagemConvencional = raw.prazoPostagemConvencional ? +raw.prazoPostagemConvencional : null;
      correios.usuarioLogisticaReversa = raw.usuarioLogisticaReversa || null;
      correios.senhaLogisticaReversa = raw.senhaLogisticaReversa || null;
      correios.prazoPostagemReversa = raw.prazoPostagemReversa ? +raw.prazoPostagemReversa : null;
      request.correios = correios;
    }

    this.isSaving = true;
    const salvar$ = this.isEdicao
      ? this.formaEnvioService.alterar(this.idFormaEnvio, request)
      : this.formaEnvioService.incluir(request);
    salvar$.subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.alertService.show('Atenção', res.errorDescription || 'Erro ao salvar.', AlertType.warning);
        } else {
          this.alertService.show('Sucesso', 'Forma de envio salva com sucesso.', AlertType.success);
          this.voltar();
        }
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
      },
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/vendas/formas-envio']);
  }

  // --- Formas de Frete ---

  public adicionarFrete(): void {
    this.abrirFreteDialog(null);
  }

  public editarFrete(index: number): void {
    this.abrirFreteDialog(this.formasFrete[index], index);
  }

  private abrirFreteDialog(item: FormaFreteItem | null, index?: number): void {
    const data: FormaFreteDialogData = { item, tiposEntrega: this.tiposEntrega };
    this.dialog.open(FormaFreteDialogComponent, { data, width: '520px' }).afterClosed().subscribe(result => {
      if (!result) return;
      if (index !== undefined) {
        this.formasFrete = this.formasFrete.map((f, i) => (i === index ? result : f));
      } else {
        this.formasFrete = [...this.formasFrete, result];
      }
    });
  }

  public removerFrete(index: number): void {
    this.formasFrete = this.formasFrete.filter((_, i) => i !== index);
  }

  // --- Buscar Serviços (Correios) ---

  public buscarServicos(): void {
    const raw = this.form.getRawValue();

    const faltaAutenticacao = !raw.usuarioCorreios || !raw.senhaCorreios || (raw.utilizarOutroCnpj && !raw.cnpjCorreios);
    const faltaContrato = !raw.numeroContrato;

    if (faltaAutenticacao || faltaContrato) {
      this.alertService.show(
        'Atenção',
        'Antes de buscar os serviços, preencha as Configurações de autenticação e as Configurações do contrato dos Correios.',
        AlertType.warning
      );
      return;
    }

    const request: BuscarServicosLogisticaRequest = {
      idTipoLogistica: raw.idTipoLogistica,
      usuarioCorreios: raw.usuarioCorreios || null,
      senhaCorreios: raw.senhaCorreios || null,
      cnpjCorreios: raw.cnpjCorreios || null,
      numeroContrato: raw.numeroContrato || null,
      codigoAdministrativo: raw.codigoAdministrativo || null,
      numeroCartaoPostagem: raw.numeroCartaoPostagem || null,
    };

    this.isBuscarFretesLoading = true;
    this.formaEnvioService.buscarServicos(request).subscribe({
      next: res => {
        this.isBuscarFretesLoading = false;
        if (res.isError) {
          this.alertService.show('Não foi possível buscar', res.errorDescription || 'Erro ao buscar os serviços.', AlertType.error);
          return;
        }
        this.abrirSelecaoServicos(res.servicos);
      },
      error: () => {
        this.isBuscarFretesLoading = false;
        this.alertService.show('Não foi possível buscar', 'Erro ao conectar com os Correios. Tente novamente.', AlertType.error);
      },
    });
  }

  private abrirSelecaoServicos(servicos: ServicoLogisticaItem[]): void {
    const jaAdicionados = this.formasFrete.map(f => f.codigoServico).filter(Boolean) as string[];
    const data: BuscarFretesDialogData = { servicos, jaAdicionados };
    this.dialog.open(BuscarFretesDialogComponent, { data, width: '560px', maxHeight: '80vh' }).afterClosed().subscribe(selecionados => {
      if (!selecionados?.length) return;
      const novos: FormaFreteItem[] = selecionados.map((s: ServicoLogisticaItem) => ({
        idFormaFrete: 0,
        descricao: s.descricao,
        codigoServico: s.codigo,
        codigoSegmento: s.codigoSegmento,
        descSegmento: s.descSegmento,
        idTipoEntrega: null,
        idPessoaTransportador: null,
      }));
      this.formasFrete = [...this.formasFrete, ...novos];
    });
  }

  // --- Logística Reversa ---

  public adicionarServicoReversa(): void {
    this.abrirReversaDialog(null);
  }

  public editarServicoReversa(index: number): void {
    this.abrirReversaDialog(this.servicosReversa[index], index);
  }

  private abrirReversaDialog(item: ServicoLogisticaReversaItem | null, index?: number): void {
    const data: ServicoReversaDialogData = { item };
    this.dialog.open(ServicoReversaDialogComponent, { data, width: '460px' }).afterClosed().subscribe(result => {
      if (!result) return;
      if (index !== undefined) {
        this.servicosReversa = this.servicosReversa.map((s, i) => (i === index ? result : s));
      } else {
        this.servicosReversa = [...this.servicosReversa, result];
      }
    });
  }

  public removerServicoReversa(index: number): void {
    this.servicosReversa = this.servicosReversa.filter((_, i) => i !== index);
  }

  public getNomeTipoEntrega(idTipoEntrega: number | null): string {
    if (!idTipoEntrega) return '—';
    return this.tiposEntrega.find(t => t.idTipoEntrega === idTipoEntrega)?.descricao ?? '—';
  }
}
